import { useCallback } from 'react';

import { MenuObjectTypes } from 'enums/menu';
import { isCombo, isItem } from 'utils/menu';
import { getDefaultComboSlotOption } from 'utils/menu/get-default-combo-slot-option';
import { ModifierComponentStyle } from 'utils/menu/modifiers';
import {
  isOptionInComboSlot,
  priceForItemOptionModifierBooleanOrStepper,
  priceForItemOptionModifierSelection,
} from 'utils/menu/price';

import {
  IPriceForComboSlotSelectionFn,
  IPriceForItemInComboSlotSelectionFn,
  IPriceForItemOptionModifierFn,
  IPricingFunctionFn,
  IUsePricingFunctionHook,
} from './types';
import {
  priceForComboSelections,
  priceForItemModifierSelections,
  priceForPremiumComboSlotDelta,
  pricingFunctionNoSelections,
} from './utils';

export const usePricingFunction: IUsePricingFunctionHook = ({
  prices: storeContextPrices,
  vendor: storeContextVendor,
}) => {
  const priceForItemInComboSlotSelection = useCallback<IPriceForItemInComboSlotSelectionFn>(
    ({ comboSlot, selectedItem, prices = storeContextPrices, vendor = storeContextVendor }) => {
      const defaultComboSlotOption = getDefaultComboSlotOption(comboSlot);
      return priceForPremiumComboSlotDelta(defaultComboSlotOption, selectedItem, prices, vendor);
    },
    [storeContextPrices, storeContextVendor]
  );

  const priceForComboSlotSelection = useCallback<IPriceForComboSlotSelectionFn>(
    ({ comboSlot, selectedOption, prices = storeContextPrices, vendor = storeContextVendor }) => {
      // return instantly if the selectedOption doesn't exist in the comboSlot... Returning zero. Its on the caller to identify what to do.
      if (!selectedOption || !isOptionInComboSlot(comboSlot, selectedOption)) {
        return 0;
      }
      const selectedItem = selectedOption?.option;

      if (!selectedItem || selectedItem._type === MenuObjectTypes.PICKER) {
        return 0;
      }
      return priceForItemInComboSlotSelection({
        comboSlot,
        selectedItem,
        prices,
        vendor,
      });
    },
    [priceForItemInComboSlotSelection, storeContextPrices, storeContextVendor]
  );

  const priceForItemOptionModifier = useCallback<IPriceForItemOptionModifierFn>(
    ({ item, itemOption, modifier, prices = storeContextPrices }) => {
      if (!item || !itemOption || !modifier || isCombo(item)) {
        return 0;
      }

      if (
        !itemOption.componentStyle ||
        itemOption.componentStyle === ModifierComponentStyle.Selector
      ) {
        return priceForItemOptionModifierSelection({
          item,
          itemOption,
          modifier,
          prices,
          vendor: storeContextVendor,
        });
      }

      return priceForItemOptionModifierBooleanOrStepper({
        item,
        itemOption,
        modifier,
        prices,
        vendor: storeContextVendor,
      });
    },
    [storeContextPrices, storeContextVendor]
  );

  /**
   * Returns the price of a menu object
   */
  // TODO: change to named params
  const pricingFunction = useCallback<IPricingFunctionFn>(
    ({
      item,
      quantity = 1,
      modifierSelections = [],
      comboSlotSelections = {},
      prices = storeContextPrices,
      vendor = storeContextVendor,
    }) => {
      const itemQuantity = quantity || 1;
      const price = pricingFunctionNoSelections(item, prices, vendor, itemQuantity);

      if (isItem(item)) {
        return (
          price +
          priceForItemModifierSelections({
            item,
            quantity,
            modifierSelections,
            prices,
            priceForItemOptionModifier,
          })
        );
      }

      if (isCombo(item)) {
        return (
          price +
          priceForComboSelections({
            combo: item,
            quantity,
            modifierSelections,
            comboSlotSelections,
            prices,
            vendor,
            priceForComboSlotSelection,
            priceForItemOptionModifier,
          })
        );
      }

      return price;
    },
    [priceForComboSlotSelection, priceForItemOptionModifier, storeContextPrices, storeContextVendor]
  );

  return {
    priceForItemInComboSlotSelection,
    priceForComboSlotSelection,
    priceForItemOptionModifier,
    pricingFunction,
  };
};
